import { normalizeBaseId } from '../lib/courseIdFormatter';
import type { GroupedCourse, RutgersCourse, Schedule, ScheduledCourse } from '../types/interfaces';

export interface HydratedActivity { id: string; title: string; day: string; startMinute: number; endMinute: number; campus: string }
export interface OwnedScheduleProjection {
  selectedCourses: GroupedCourse[];
  pinnedClasses: string[];
  customActivities: HydratedActivity[];
}

/** Existing configuration/legacy reconstruction, using only this receipt's catalog.
 * No visible allCourses closure, IO, record mutation or new course eligibility policy.
 */
export function reconstructOwnedSchedule(
  record: Schedule, catalog: RutgersCourse[], mapSection: (course: RutgersCourse) => ScheduledCourse,
): OwnedScheduleProjection {
  const lookup = (baseId: string) => catalog.filter(course => course.baseId === baseId);
  const config = record.configuration;
  if (config) {
    const selectedCourses = (Array.isArray(config.selectedCourses) ? config.selectedCourses : []).map((simple: {
      id: string; name: string; isCourseList?: boolean; courseListId?: string | number; quantity?: number;
      selectedSectionId?: string; allowedSections?: string[]; allowedCourses?: Record<string, string[]>;
    }): GroupedCourse | null => {
      if (simple.isCourseList) return {
        id: simple.id, name: simple.name, sections: [], selectedSection: undefined, isCourseList: true,
        courseList: { id: simple.courseListId?.toString() || '', name: simple.name, classes: [], created_at: '', is_public: true, user_generated: '' },
        quantity: simple.quantity || 1, selectedSectionId: simple.selectedSectionId,
        allowedSections: simple.allowedSections, allowedCourses: simple.allowedCourses ?? {},
      } as GroupedCourse;
      const normalized = normalizeBaseId(simple.id);
      if (!catalog.some(course => normalizeBaseId(course.baseId) === normalized)) return null;
      const sections = lookup(normalized.replace(/:/g, '')).map(mapSection);
      return { id: simple.id, name: simple.name, sections,
        selectedSection: simple.selectedSectionId ? sections.find(section => section.id === simple.selectedSectionId) || sections[0] : sections[0],
        isCourseList: false, quantity: simple.quantity, allowedSections: simple.allowedSections, allowedCourses: simple.allowedCourses } as GroupedCourse;
    }).filter((course: GroupedCourse | null): course is GroupedCourse => course !== null);
    return { selectedCourses, pinnedClasses: Array.isArray(config.pinnedClasses) ? [...config.pinnedClasses] : [],
      customActivities: Array.isArray(config.customActivities) ? config.customActivities : [] };
  }
  const selectedCourses: GroupedCourse[] = [], seen = new Set<string>();
  for (const reference of record.classes ?? []) {
    const parts = reference.split(':');
    if (parts.length !== 2) continue; // Preserve the current initializer's legacy grammar.
    const [baseId, sectionId] = parts;
    if (seen.has(baseId)) continue;
    const rows = lookup(baseId); if (!rows.length) continue;
    const saved = rows.find(section => section.id === sectionId) ?? rows[0];
    selectedCourses.push({ id: baseId, name: saved.courseName, sections: rows.map(mapSection), selectedSection: mapSection(saved) });
    seen.add(baseId);
  }
  return { selectedCourses, pinnedClasses: [], customActivities: [] };
}
